import { useEffect, useEffectEvent, useRef, useState, type ReactNode } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useApplyFieldErrors, useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import { FormLayout, SummaryCard, TagInput } from '@nucleus-commerce/admin-kit/forms';
import { usePermissions } from '@nucleus-commerce/admin-kit/permissions';
import { useSaveBar } from '@nucleus-commerce/admin-kit/save-bar';
import type { StoreProduct, StoreProductSchedule } from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Input } from '@nucleus-commerce/ui/components/input';

import { MetadataFields, ScopeChip, type SharedProductSections } from '@/features/products';
import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';

import { fromStoreProduct, toStoreProductUpdate } from '../api/mappers';
import { useUpdateStoreProduct } from '../hooks/use-store-product-mutations';
import { changedFields } from '../schemas/form-changes';
import { publishConflict } from '../schemas/publication-rules';
import {
  storeProductFormSchema,
  type StoreProductFormInput,
  type StoreProductFormOutput,
} from '../schemas/store-product-form-schema';

import { FeaturedOverrideNotice } from './featured-override-notice';
import { StoreBasicsCard } from './store-basics-card';
import { StoreSearchEnginesCard } from './store-search-engines-card';
import { StoreStatusCard } from './store-status-card';

export const STORE_PRODUCT_FORM_ID = 'store-product-form';

export interface StoreProductFormProps {
  storeId: string;
  row: StoreProduct;
  /** The shared gallery, given the note about a featured image of this store's own, if any. */
  media: (notice: ReactNode) => ReactNode;
  /** The Variants card, between Options and Diamond selection. */
  variants?: ReactNode;
  /** The Collections card, between Customizations and Fulfilment. */
  collections?: ReactNode;
  /** Under Shipping: the Stores and Insights cards. */
  railEnd?: ReactNode;
  /** The master's own cards, which write the master; absent if it could not be read. */
  shared?: SharedProductSections | undefined;
  /**
   * The rotation that owns this store product, if any: its runs set the status, so the one-shot
   * publish and unpublish dates are off (the backend refuses them with SCHEDULE_CONFLICT).
   */
  owner?: StoreProductSchedule | null;
}

/**
 * One publication of a product, laid out and ordered like the master product's page with the
 * master's own cards in between. This form holds the store's own fields; a 409 for a non-active
 * master product is explained in the Status card. Cards of this store sit in several <form>s that
 * submit through the same handler, so Enter saves wherever they are; the master's cards bring their
 * own form, and the one save bar saves whichever of the two changed. The form stays
 * mounted for the page's life, because the Variants card under it holds drafts of its own: a save
 * or a newer row re-seeds it in place and never takes a field that changed in the meantime.
 */
export function StoreProductForm({
  storeId,
  row,
  media,
  variants,
  collections,
  railEnd,
  shared,
  owner = null,
}: StoreProductFormProps) {
  const { t } = useTranslation('storeCatalog');
  const message = useErrorMessage();
  const format = useFormat();
  const { can } = usePermissions();
  const canWrite = can('products.write');
  const update = useUpdateStoreProduct(storeId);
  const form = useForm<StoreProductFormInput, unknown, StoreProductFormOutput>({
    resolver: useI18nResolver(storeProductFormSchema),
    defaultValues: fromStoreProduct(row, format.timeZone),
    disabled: !canWrite,
  });
  // Bumped when the page goes back to a clean state, so overrides opened on it close.
  const [settled, setSettled] = useState(0);
  const seeded = useRef(form.getValues());
  /** Takes `next` as the saved state, keeping what differs from `since`: unsaved, still dirty. */
  const reseed = (next: StoreProductFormInput, since: StoreProductFormInput) => {
    const current = form.getValues();
    const kept = changedFields(current, since);
    form.reset(next);
    seeded.current = next;
    for (const field of kept) {
      form.setValue(field, current[field], { shouldDirty: true });
    }
  };
  const applyFieldErrors = useApplyFieldErrors(form.setError);
  const master = row.product;
  const featuredMediaId = form.watch('featuredMediaId');

  // A refetch with a newer row, or a change from elsewhere on the page. During this form's own
  // save the row changes too; the save itself re-seeds when it's done.
  const seenRow = useRef(row);
  const reseedFromRow = useEffectEvent(() => {
    if (update.isPending) return;
    reseed(fromStoreProduct(row, format.timeZone), seeded.current);
  });
  useEffect(() => {
    if (seenRow.current === row) return;
    seenRow.current = row;
    reseedFromRow();
  }, [row]);

  const submit = form.handleSubmit(async (values) => {
    form.clearErrors('root.server');
    const submitted = structuredClone(form.getValues());
    try {
      const saved = await update.mutateAsync({
        id: row.id,
        body: toStoreProductUpdate(values, row, format.timeZone),
      });
      reseed(fromStoreProduct(saved, format.timeZone), submitted);
      setSettled((count) => count + 1);
    } catch (error) {
      if (publishConflict(error)) return;
      if (!applyFieldErrors(error)) {
        form.setError('root.server', { type: 'server', message: message(error) });
      }
    }
  });
  useSaveBar({
    form,
    onSave: submit,
    onDiscard: () => {
      form.reset();
      setSettled((count) => count + 1);
    },
    saving: update.isPending,
    disabled: !canWrite,
  });

  const rootError = form.formState.errors.root?.server?.message;
  // Inside the shared Organization card, whose own context is the master's: the fields read this
  // provider, the nearest one.
  const storeFields = (
    <Form {...form}>
      <form onSubmit={submit} noValidate className="flex flex-col gap-3 border-t pt-3">
        <span className="flex items-center gap-2 text-[13px] font-semibold">
          {t('storePage.organisation.title')}
          <ScopeChip scope="store" />
        </span>
        <FormField
          control={form.control}
          name="tags"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('fields.tags')}</FormLabel>
              <FormControl>
                <TagInput
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  placeholder={t('fields.tagsPlaceholder')}
                  disabled={!canWrite}
                  max={100}
                  removeLabel={(tag) => t('fields.removeTag', { tag })}
                />
              </FormControl>
              <FormDescription>{t('fields.tagsHint')}</FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="position"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('storePage.organisation.position')}</FormLabel>
              <FormControl>
                <Input
                  inputMode="numeric"
                  {...field}
                  placeholder={t('storePage.organisation.positionPlaceholder')}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </form>
    </Form>
  );

  return (
    <Form {...form}>
      <FormLayout
        aside={
          <>
            <StoreStatusCard
              productId={row.productId}
              masterStatus={master.status}
              saveError={update.error}
              owner={owner}
            />
            {shared ? (
              shared.organisation(storeFields)
            ) : (
              <SummaryCard title={t('storePage.organisation.title')}>{storeFields}</SummaryCard>
            )}
            {shared?.customizations}
            {collections}
            {shared?.fulfilment}
            {shared?.shipping}
            {railEnd}
          </>
        }
      >
        <form
          id={STORE_PRODUCT_FORM_ID}
          onSubmit={submit}
          noValidate
          className="flex flex-col gap-6"
        >
          {shared?.saveError}
          {rootError ? (
            <Alert variant="destructive" role="alert">
              <AlertDescription>{rootError}</AlertDescription>
            </Alert>
          ) : null}
          <StoreBasicsCard master={master} settled={settled} />
        </form>
        {media(featuredMediaId ? <FeaturedOverrideNotice /> : null)}
        {shared?.attributes}
        {shared?.options}
        {variants}
        {shared?.diamond}
        {shared?.related}
        <StoreSearchEnginesCard storeId={storeId} row={row} shared={shared} />
        <form onSubmit={submit} noValidate>
          <MetadataFields
            description={t('sections.metadataDescription')}
            badge={<ScopeChip scope="store" />}
          />
        </form>
      </FormLayout>
    </Form>
  );
}
